/**
 * ProgressHeader – Progress card for phase/step views.
 * Figma Frame 354: bordered card with bold teal title + "progress",
 * "N of M tasks completed", percentage on right, and progress bar.
 */

import TimeEstimate from './TimeEstimate';
import { SETTLEMENT_PLANNER_PROGRESS } from './settlementTheme';

interface ProgressHeaderProps {
  title: string;
  subtitle?: string;
  /** Optional intro/supporting copy displayed under the subtitle */
  intro?: string;
  variant?: 'default' | 'planner';
  completedSteps: number;
  totalSteps: number;
  /** Remaining or total estimated minutes for the phase (shown when > 0) */
  estimatedMinutes?: number;
  /** Optional second line under the title row (e.g. in-step tracker label) */
  trackerLabel?: string;
  /** Optional tracker counts for a nested progress bar */
  trackerCompleted?: number;
  trackerTotal?: number;
  /** Label used in N of M completed line (default tasks) */
  completedItemLabel?: string;
}

export default function ProgressHeader({
  title,
  subtitle,
  intro,
  variant = 'default',
  completedSteps,
  totalSteps,
  estimatedMinutes,
  trackerLabel,
  trackerCompleted = 0,
  trackerTotal = 0,
  completedItemLabel = 'tasks',
}: ProgressHeaderProps) {
  const percentage = totalSteps > 0 ? Math.round((completedSteps / totalSteps) * 100) : 0;
  const trackerPct =
    trackerTotal > 0 ? Math.round((Math.min(trackerCompleted, trackerTotal) / trackerTotal) * 100) : 0;
  const isPlannerVariant = variant === 'planner';
  const cardClassName = isPlannerVariant
    ? SETTLEMENT_PLANNER_PROGRESS.card
    : 'rounded-xl border border-blue-300 bg-white-alt p-[25px]';
  const progressTitleClassName = isPlannerVariant
    ? SETTLEMENT_PLANNER_PROGRESS.progressTitle
    : 'font-bold text-blue-600';
  const titleAccentClassName = isPlannerVariant ? SETTLEMENT_PLANNER_PROGRESS.titleAccent : 'font-bold text-blue-600';
  const titleLabelClassName = isPlannerVariant ? SETTLEMENT_PLANNER_PROGRESS.titleLabel : 'text-black font-normal';
  const countClassName = isPlannerVariant ? SETTLEMENT_PLANNER_PROGRESS.count : 'text-xs text-grey-600';
  const percentClassName = isPlannerVariant ? SETTLEMENT_PLANNER_PROGRESS.percent : 'text-base font-bold text-blue-600';
  const progressTrackClassName = isPlannerVariant ? SETTLEMENT_PLANNER_PROGRESS.track : 'w-full bg-grey-200 rounded-full h-[7px]';
  const progressFillClassName = isPlannerVariant
    ? SETTLEMENT_PLANNER_PROGRESS.fill
    : 'bg-blue-600 h-[7px] rounded-full transition-all duration-500';

  return (
    <div className={isPlannerVariant ? 'mb-5' : 'mb-6'}>
      {subtitle && (
        <p className={`whitespace-pre-line ${isPlannerVariant ? 'text-stone-800 text-lg font-medium leading-relaxed mb-3' : 'text-grey-600 text-sm mb-3'}`}>{subtitle}</p>
      )}
      {intro && (
        <p className={`whitespace-pre-line ${isPlannerVariant ? 'text-stone-700 text-sm leading-relaxed mb-5' : 'text-grey-600 text-sm mb-4'}`}>{intro}</p>
      )}

      {/* Progress card — Figma: bordered card, 25px padding */}
      <div className={cardClassName}>
        {/* Title row */}
        <div className="flex flex-wrap items-baseline justify-between gap-2 mb-1">
          <p className="text-base">
            {isPlannerVariant ? (
              <span className={progressTitleClassName}>Progress</span>
            ) : (
              <>
                <span className={titleAccentClassName}>{title}</span>
                <span className={titleLabelClassName}> progress</span>
              </>
            )}
          </p>
          {estimatedMinutes != null && estimatedMinutes > 0 && (
            <TimeEstimate minutes={estimatedMinutes} className={isPlannerVariant ? 'text-stone-600' : 'text-grey-600'} />
          )}
        </div>

        {/* Count + percentage row */}
        <div className="flex items-center justify-between mb-2">
          <span className={countClassName}>
            {completedSteps} of {totalSteps} {completedItemLabel} completed
          </span>
          <span className={percentClassName}>{percentage}%</span>
        </div>

        {/* Progress bar — Figma: ~7px height */}
        <div className={progressTrackClassName}>
          <div
            className={progressFillClassName}
            style={{ width: `${percentage}%` }}
          />
        </div>

        {trackerLabel != null && trackerTotal > 0 && (
          <div className="mt-4 pt-4 border-t border-grey-200">
            <div className="flex items-center justify-between mb-1">
              <span className={isPlannerVariant ? SETTLEMENT_PLANNER_PROGRESS.trackerTitle : 'text-xs font-medium text-grey-700'}>{trackerLabel}</span>
              <span className={isPlannerVariant ? SETTLEMENT_PLANNER_PROGRESS.trackerCount : 'text-xs text-grey-600'}>
                {trackerCompleted} of {trackerTotal}
              </span>
            </div>
            <div className={isPlannerVariant ? SETTLEMENT_PLANNER_PROGRESS.trackerTrack : 'w-full bg-grey-200 rounded-full h-[5px]'}>
              <div
                className={isPlannerVariant ? SETTLEMENT_PLANNER_PROGRESS.trackerFill : 'bg-blue-500 h-[5px] rounded-full transition-all duration-500'}
                style={{ width: `${trackerPct}%` }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
